import { useEffect, useState, Suspense, lazy, useMemo } from 'react';
import {
  useNavigate,
  useOutletContext,
  useParams,
  useLocation,
  useSearchParams,
  Link,
} from 'react-router-dom';
import {
  getClientById,
  updateClientActive,
  getClaveUnicaByClientId,
} from '../../../services/clientService';
import './clientsDetail.scss';
import {
  getCausasRutBajasByClient,
  updateCausasRutBajas,
} from '../../../services/caseServices';
import Message from '../../../components/Message/Message';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import FloatingAlert from '../../../components/Alert/FloatingAlert';
import ActiveInactiveSwitch from '../../../components/ActiveInactiveSwitch/ActiveInactiveSwitch';
import ClientsCreateModal from '../ClientsCreateModal';
import { getCurrentUser } from '../../../services/authService';
import usePermissionsCrm from 'permissions/modules/crm/usePermissionsCrm';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { clientsDetailI18n } from './i18n';

const parseBajaStatus = (data) => {
  if (typeof data === 'boolean') {
    return { hasAnyBaja: data, allBaja: data, categoryText: null };
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const hasAnyBaja = Boolean(data.tieneClasificacionBaja);
    return {
      hasAnyBaja,
      allBaja: hasAnyBaja,
      categoryText: data.categoryName || null,
    };
  }

  const entries = Array.isArray(data) ? data : data ? [data] : [];
  if (entries.length === 0) {
    return { hasAnyBaja: false, allBaja: false, categoryText: null };
  }

  const bajaEntries = entries.filter(
    (item) => item?.tieneClasificacionBaja === true
  );
  const hasAnyBaja = bajaEntries.length > 0;
  const allBaja = entries.every(
    (item) => item?.tieneClasificacionBaja === true
  );
  const categoryText = [
    ...new Set(
      bajaEntries
        .map((item) => item?.categoryName)
        .filter((value) => value && String(value).trim())
    ),
  ].join(', ');

  return {
    hasAnyBaja,
    allBaja,
    categoryText: categoryText || null,
  };
};

const ClientsDetail = () => {
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, Badge, SvgIcon, IconButton, Modal } =
    DesignSystemComponents ?? {};

  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useScopedTranslation(clientsDetailI18n);
  const flagPanel = state?.flagPanel;

  const [activeTabInfo, setActiveTabInfo] = useState('general');
  const [activeTabTasks, setActiveTabTasks] = useState('tasks');
  const [client, setClient] = useState(null);
  const [clientRaw, setClientRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientRut, setClientRut] = useState(false);
  const [clientAllBaja, setClientAllBaja] = useState(false);
  const [clientBajaCategory, setClientBajaCategory] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [clientCases, setClientCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [casesError, setCasesError] = useState(null);
  const [bajaHasClassification, setBajaHasClassification] = useState(false);
  const [blockManagerAny, setBlockManagerAny] = useState(false);
  const [blockManagerAll, setBlockManagerAll] = useState(false);
  const [clientActive, setClientActive] = useState(null);
  const [clientPatrocinio, setClientPatrocinio] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusAlert, setStatusAlert] = useState(null);
  const [temporarilyActivating, setTemporarilyActivating] = useState(false);
  const [showTempActivateConfirm, setShowTempActivateConfirm] = useState(false);
  const [taskIdToOpen, setTaskIdToOpen] = useState(null);
  const [toggleSectionActive, setToggleSectionActive] = useState('first');
  const [showPassword, setShowPassword] = useState(false);
  const [loadingClaveUnica, setLoadingClaveUnica] = useState(false);

  const handleToggleClaveUnica = async () => {
    if (showPassword) {
      setShowPassword(false);
      return;
    }
    try {
      setLoadingClaveUnica(true);
      const data = await getClaveUnicaByClientId(id);
      const value =
        typeof data === 'string'
          ? data
          : (data?.claveUnica ?? data?.value ?? '--');
      setClient((prev) => (prev ? { ...prev, claveUnica: value } : prev));
      setShowPassword(true);
    } catch (error) {
      console.error('Error fetching clave única:', error);
    } finally {
      setLoadingClaveUnica(false);
    }
  };

  const currentUserId = useMemo(() => {
    const currentUser = getCurrentUser?.();
    return (
      currentUser?.id ??
      currentUser?.userId ??
      currentUser?.usuarioAsignadoId ??
      null
    );
  }, []);

  const {
    canEditClient,
    canViewInactiveBlockedClient,
    canToggleClientActive,
    canAccessGeneralInformation,
    canAccessClientCalls,
    canAccessClientEmails,
    canAccessClientContracts,
    canAccessClientDocuments,
    canAccessClientSacEeppTasks,
    canAccessClientSac,
    canAccessClientSacLegacy,
    canAccessClientEepp,
    canAccessClientEeppLegacy,
  } = usePermissionsCrm();

  const canBypassClientBlock = canViewInactiveBlockedClient;

  useEffect(() => {
    if (!statusAlert) return undefined;
    const timeout = setTimeout(() => setStatusAlert(null), 3000);
    return () => clearTimeout(timeout);
  }, [statusAlert]);

  useEffect(() => {
    if (!id) {
      setError(t('cd_error_invalid_id'));
      setLoading(false);
      return;
    }

    const abortController = new AbortController();

    const fetchClient = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getClientById(id, abortController.signal);
        const result = await getCausasRutBajasByClient(data.id);
        const { hasAnyBaja, allBaja, categoryText } = parseBajaStatus(result);

        setClientRut(hasAnyBaja);
        setClientAllBaja(allBaja);
        setClientBajaCategory(categoryText);
        setClientPatrocinio(data.patrocinio || null);
        setClientRaw(data);
        setClientActive(Boolean(data.active));
        // Mapear datos del API al formato del componente
        const clientData = {
          id: data.identifier || t('cd_na'),
          realId: data.id, // Guardamos el ID real para las llamadas
          name: data.name || t('cd_no_name'),
          status: data.active ? t('cd_status_active') : t('cd_status_inactive'),
          claveUnica: data.claveUnica || '--',
          contact: {
            phone: data.telephone || t('cd_na'),
            email: data.email || t('cd_na'),
            address: data.address || t('cd_na'),
          },
          sac: {
            agent: data.sacName || t('cd_unassigned'),
            branch: data.branchName || data.branchName2 || t('cd_na'),
          },
          stats: {
            since: data.create_At
              ? new Date(data.create_At).toLocaleDateString()
              : t('cd_na'),
            activeCases: 0,
            totalCalls: 0,
          },
        };

        setClient(clientData);

        const cases = Array.isArray(result?.cases) ? result.cases : [];
        const filteredCases = cases.filter(
          (item) => item?.businessAreaId !== 2 && item?.businessAreaId !== 11
        );
        setClientCases(filteredCases);
        setBajaHasClassification(Boolean(result?.tieneClasificacionBaja));
        const anyBlocked = filteredCases.some((item) => item?.blockManager);
        const allBlocked =
          filteredCases.length > 0 &&
          filteredCases.every((item) => item?.blockManager);
        setBlockManagerAny(anyBlocked);
        setBlockManagerAll(allBlocked);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message || t('cd_error_load'));
          console.error('Error fetching client:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchClient();

    return () => abortController.abort();
  }, [id, refreshKey]);

  useEffect(() => {
    if (clientRaw?.id) {
      setCasesLoading(false);
      setCasesError(null);
    }
  }, [clientRaw?.id]);

  const getCaseFields = (item, index) => {
    const area =
      item?.area ||
      item?.businessArea?.name ||
      item?.businessArea ||
      item?.businessLine ||
      item?.lineaNegocio ||
      item?.group ||
      t('cd_na');
    const title =
      item?.title || item?.caseTitle || item?.name || t('cd_case_label', { index: index + 1 });
    const code = item?.code || item?.rol || item?.caseCode || t('cd_na');
    const court = item?.tribunal || t('cd_na');

    return { area, title, code, court };
  };

  const editInitialData = useMemo(() => {
    if (!clientRaw) return null;
    return {
      clientId: clientRaw.id ?? client?.realId ?? '',
      fullName: clientRaw.name ?? client?.name ?? '',
      identifier: clientRaw.identifier ?? client?.id ?? '',
      secretKey: clientRaw.claveUnica ?? '',
      email: clientRaw.email ?? client?.contact?.email ?? '',
      phone: clientRaw.telephone ?? client?.contact?.phone ?? '',
      phone2: clientRaw.phone2 ?? clientRaw.telephone2 ?? '',
      address: clientRaw.address ?? '',
      nationality: clientRaw.idCountry ?? '',
      entityType: clientRaw.idIdentityType ?? '',
      gender: clientRaw.idGenre ?? '',
      maritalStatus: clientRaw.idMaritalStatus ?? '',
      branchOffice: clientRaw.idBranchOffice ?? '',
      region: clientRaw.idRegion ?? '',
      sacLawyer: clientRaw.userIdSac ?? clientRaw.newSac ?? '',
      notes: clientRaw.observations ?? '',
      updaterId: currentUserId,
    };
  }, [clientRaw, client, currentUserId]);

  const statusColor = (active) => (active ? 'success' : 'light');

  const handleClientActiveChange = async (nextActive) => {
    if (statusUpdating || nextActive === clientActive) return;

    const clientId = clientRaw?.id ?? client?.realId ?? null;
    if (!clientId) {
      setStatusAlert({
        type: 'danger',
        message: t('cd_error_identify_client'),
      });
      return;
    }

    if (!currentUserId) {
      setStatusAlert({
        type: 'danger',
        message: t('cd_error_identify_user'),
      });
      return;
    }

    const previousActive = clientActive;
    setClientActive(nextActive);
    setClientRaw((prev) => (prev ? { ...prev, active: nextActive } : prev));
    setClient((prev) =>
      prev ? { ...prev, status: nextActive ? t('cd_status_active') : t('cd_status_inactive') } : prev
    );
    setStatusUpdating(true);

    try {
      await updateClientActive({
        clientId,
        active: nextActive ? 1 : 0,
        updaterId: currentUserId,
      });
      setStatusAlert({
        type: 'success',
        message: nextActive
          ? t('cd_client_activated')
          : t('cd_client_deactivated'),
      });
    } catch (err) {
      setClientActive(previousActive);
      setClientRaw((prev) =>
        prev ? { ...prev, active: previousActive } : prev
      );
      setClient((prev) =>
        prev
          ? { ...prev, status: previousActive ? t('cd_status_active') : t('cd_status_inactive') }
          : prev
      );
      setStatusAlert({
        type: 'danger',
        message:
          err?.response?.data?.message ||
          err?.message ||
          t('cd_error_update_status'),
      });
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleTemporarilyActivate = async () => {
    if (temporarilyActivating || !clientRaw?.identifier) return;

    setTemporarilyActivating(true);
    const rut = clientRaw.identifier;

    try {
      await updateCausasRutBajas(rut);

      setStatusAlert({
        type: 'success',
        message: t('cd_temp_activate_success'),
      });

      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      setStatusAlert({
        type: 'danger',
        message:
          error?.response?.data?.message ||
          error?.message ||
          t('cd_temp_activate_error'),
      });
    } finally {
      setTemporarilyActivating(false);
      setShowTempActivateConfirm(false);
    }
  };

  const tabsInfo = [
    {
      key: 'general',
      label: t('cd_tab_general'),
      canAccess: canAccessGeneralInformation,
    },
    { key: 'calls', label: t('cd_tab_calls'), canAccess: canAccessClientCalls },
    { key: 'emails', label: t('cd_tab_emails'), canAccess: canAccessClientEmails },
    // { key: 'billing', label: t('cd_billing_title') },
    { key: 'contract', label: t('cd_tab_contract'), canAccess: canAccessClientContracts },
    {
      key: 'documents',
      label: t('cd_tab_documents'),
      canAccess: canAccessClientDocuments,
    },
  ];

  const tabImportMapInfo = {
    general: () => import('./tabs/General/ClientGeneral.jsx'),
    calls: () => import('./tabs/ClientsCalls.jsx'),
    emails: () => import('./tabs/ClientsEmails.jsx'),
    // billing: () => import('./tabs/Billing/ClientsBilling.jsx'),
    contract: () => import('./tabs/Contracts/ClientsContracts.jsx'),
    documents: () => import('./tabs/ClientsDocs.jsx'),
  };

  const tabsTasks = [
    { key: 'tasks', label: t('cd_tab_sac'), canAccess: canAccessClientSac },
    {
      key: 'tasksLegacy',
      label: t('cd_tab_sac_legacy'),
      canAccess: canAccessClientSacLegacy,
    },
    { key: 'eepp', label: t('cd_tab_eepp'), canAccess: canAccessClientEepp },
    {
      key: 'eeppBeta',
      label: t('cd_tab_eepp_beta'),
      canAccess: canAccessClientEepp,
    },
    {
      key: 'eeppLegacy',
      label: t('cd_tab_eepp_legacy'),
      canAccess: canAccessClientEeppLegacy,
    },
  ];

  const tabImportMapTasks = {
    tasks: () => import('./tabs/ClientsTasks.jsx'),
    tasksLegacy: () => import('./tabs/ClientsTasksLegacy.jsx'),
    eepp: () => import('./tabs/ClientsEEPP.jsx'),
    eeppBeta: () => import('./tabs/ClientsEEPPBeta.jsx'),
    eeppLegacy: () => import('./tabs/ClientsEEPPLegacy.jsx'),
  };

  const BlockedClientMessage = () => (
    <div>
      <Message
        type='warning'
        direction='horizontal'
        title={
          <>
            {t('cd_blocked_title')} <span className='mini'></span>
          </>
        }
        description={
          <>
            {t('cd_blocked_desc_prefix')}{' '}
            <span className='fw-semibold'>{client?.name || '--'}</span> {t('cd_blocked_desc_rut', { rut: client?.id || '--' })}{' '}
            <span className='fw-semibold'>{t('cd_blocked_desc_disabled')}</span>
            <p className='mt-2 mb-0'>
              <span className='fw-semibold'>{t('cd_blocked_reason')}</span> {bajaReason}
            </p>
            <p className='fw-semibold mt-3'>{t('cd_blocked_next_steps')}</p>
            <ul className='list-auto'>
              <li className='pt-1'>
                {' '}
                {t('cd_blocked_step1')}
              </li>
              <li className='pt-1'>
                {t('cd_blocked_step2_prefix')}{' '}
                <span className='fw-semibold'>{t('cd_blocked_step2_area')}</span>{' '}
                {t('cd_blocked_step2_suffix')}
              </li>
            </ul>
          </>
        }
      >
        <Button
          variant='naked'
          size='sm'
          label={t('cd_back_clients')}
          iconStart='chevronLeft'
          onClick={() => navigate('-1')}
        />
      </Message>
    </div>
  );

  const LazyTabInfo = useMemo(() => {
    const importer = tabImportMapInfo[activeTabInfo];
    return importer ? lazy(importer) : null;
  }, [activeTabInfo]);

  const LazyTabTasks = useMemo(() => {
    const importer = tabImportMapTasks[activeTabTasks];
    return importer ? lazy(importer) : null;
  }, [activeTabTasks]);

  useEffect(() => {
    const updateTableHeight = () => {
      const h = window.innerHeight;
      let maxHeight = 498;

      if (h < 600) {
        // Zoom 150%
        maxHeight = 60;
      } else if (h < 700) {
        // Zoom 125%
        maxHeight = 73;
      } else if (h < 800) {
        // Zoom 110%
        maxHeight = 75;
      } else if (h < 900) {
        // Zoom 100%
        maxHeight = 78;
      } else if (h < 1000) {
        // Zoom 90%
        maxHeight = 80;
      } else if (h < 1100) {
        // Zoom 80%
        maxHeight = 83;
      }

      document.documentElement.style.setProperty(
        '--info-max-height',
        `${maxHeight}%`
      );
    };

    updateTableHeight();
    window.addEventListener('resize', updateTableHeight);

    return () => window.removeEventListener('resize', updateTableHeight);
  }, []);

  useEffect(() => {
    if (toggleSectionActive === 'first' && !tabImportMapInfo[activeTabInfo]) {
      setActiveTabInfo('general');
    }

    if (
      toggleSectionActive === 'second' &&
      !tabImportMapTasks[activeTabTasks]
    ) {
      setActiveTabTasks('tasks');
    }
  }, [toggleSectionActive]);

  // Deep-link de pestaña: ?tab=<key> al montar pre-selecciona la pestaña pedida.
  // Las pestañas del segundo grupo (tareas SAC/EEPP) necesitan que también se mueva
  // el toggle de sección a 'second'; las del primero, a 'first'. Se hace en el MISMO
  // efecto para evitar la carrera con el efecto de arriba (que resetea activeTabTasks
  // cuando la sección cambia). Solo al montar (deps vacías). 'eeppBeta' se honra sólo
  // si el usuario tiene el permiso de la pestaña (canAccessClientEepp).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (!tab) return;

    const secondGroupKeys = ['tasks', 'tasksLegacy', 'eepp', 'eeppBeta', 'eeppLegacy'];
    const firstGroupKeys = ['general', 'calls', 'emails', 'contract', 'documents'];

    if (secondGroupKeys.includes(tab)) {
      // 'eeppBeta' está gateada por el permiso de la pestaña: si no se puede acceder,
      // no forzamos el deep-link.
      if (tab === 'eeppBeta' && !canAccessClientEepp) return;
      setToggleSectionActive('second');
      setActiveTabTasks(tab);
    } else if (firstGroupKeys.includes(tab)) {
      setToggleSectionActive('first');
      setActiveTabInfo(tab);
    }
  }, []);

  if (!Card || !Button || !Badge) return null;

  if (loading) {
    return (
      <div className='container-client-detail'>
        <p className='caption'>{t('cd_loading')}</p>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className='container-client-detail'>
        <Button
          variant='naked'
          iconStart='chevronLeft'
          label={t('cd_back_clients')}
          size='sm'
          onClick={() => navigate(-1)}
        />
        <p className='caption text-danger mt-3'>
          {error || t('cd_not_found')}
        </p>
      </div>
    );
  }

  const hasAnyBaja = Boolean(clientRut || flagPanel);
  const hasCases = clientCases.length > 0;
  const shouldBlockClient =
    bajaHasClassification && (!hasCases || blockManagerAll);
  const shouldShowPartialBaja =
    bajaHasClassification && blockManagerAny && !blockManagerAll;
  const shouldShowManagerWarning =
    !bajaHasClassification && blockManagerAny && hasCases;
  const shouldShowMixedStateWarning =
    bajaHasClassification && !blockManagerAny && hasCases;
  const shouldShowBypassButton =
    canBypassClientBlock &&
    bajaHasClassification &&
    (!hasCases || blockManagerAll || (blockManagerAny && !blockManagerAll));
  const shouldShowPartialWithBypass =
    shouldShowPartialBaja && shouldShowBypassButton;
  const bajaReason = clientBajaCategory || t('cd_no_info');
  const hasPatrocinio = Boolean(clientPatrocinio);
  const patrocinioDetail =
    typeof clientPatrocinio === 'string' && clientPatrocinio.trim()
      ? clientPatrocinio
      : null;

  return (
    <div className='container-client-detail'>
      {statusAlert?.message && (
        <FloatingAlert
          message={statusAlert.message}
          type={statusAlert.type}
          onClose={() => setStatusAlert(null)}
        />
      )}
      <div className='d-flex align-items-center gap-2 mb-2'>
        <Button
          variant='naked'
          iconStart='chevronLeft'
          label={t('cd_back')}
          size='sm'
          onClick={() => navigate(-1)}
        />
      </div>
      <div className='d-flex flex-column mb-2'>
        <div className='d-flex justify-content-between'>
          <div className='d-flex align-items-center gap-2'>
            <h2 className='fw-medium mb-1'>{t('cd_client_file', { name: client.name })}</h2>
            <button
              type='button'
              className='btn btn-link p-0'
              onClick={() => setShowEditModal(true)}
              aria-label={t('cd_edit_client_aria')}
              disabled={!canEditClient}
            >
              <SvgIcon name='pencil' size='1rem' stroke='var(--primary-500)' />
            </button>
          </div>
          <>
            <div className='btn-group' role='group'>
              <Button
                label={t('cd_info_btn')}
                variant={`${toggleSectionActive === 'first' ? 'primary' : 'secondary'}`}
                onClick={() => {
                  setToggleSectionActive('first');
                  setActiveTabInfo('general');
                }}
                className='btn-group-individual'
                size='sm'
              />
              <Button
                label={t('cd_tasks_sac_eepp_btn')}
                variant={`${toggleSectionActive === 'second' ? 'primary' : 'secondary'}`}
                onClick={() => {
                  setToggleSectionActive('second');
                  setActiveTabTasks('tasks');
                }}
                className='btn-group-individual'
                size='sm'
                disabled={!canAccessClientSacEeppTasks}
              />
            </div>
          </>
        </div>
        <div className='mt-3'>
          {toggleSectionActive === 'first' && (
            <div>
              <div>
                {hasPatrocinio && (
                  <div className='warning-message d-flex gap-3 align-items-center'>
                    <SvgIcon
                      name='exclamationTriangle'
                      size='1.25rem'
                      stroke='var(--warning-500)'
                    />
                    {patrocinioDetail && (
                      <span className='ms-2'>{patrocinioDetail}</span>
                    )}
                    <p className='caption fw-semibold font-light'>
                      {t('cd_temporarily_active')}
                    </p>
                  </div>
                )}
                {shouldShowPartialWithBypass && (
                  <div className='warning-message d-flex gap-3 align-items-center'>
                    <SvgIcon
                      name='exclamationTriangle'
                      size='1.25rem'
                      stroke='var(--warning-500)'
                    />
                    <span>{t('cd_client_has_baja_cases')}</span>
                    <Button
                      variant='naked'
                      size='sm'
                      disabled={temporarilyActivating}
                      label={
                        temporarilyActivating
                          ? t('cd_activating')
                          : t('cd_activate_temporarily')
                      }
                      onClick={() => setShowTempActivateConfirm(true)}
                    />
                  </div>
                )}
                {!shouldShowPartialWithBypass && shouldShowPartialBaja && (
                  <div className='warning-message'>
                    {t('cd_client_has_baja_cases')}
                  </div>
                )}
                {shouldShowMixedStateWarning && (
                  <div className='warning-message'>
                    {t('cd_mixed_state_warning')}
                  </div>
                )}
                {shouldShowManagerWarning && (
                  <div className='warning-message'>
                    {t('cd_manager_warning')}
                  </div>
                )}
                {!canBypassClientBlock && shouldBlockClient && (
                  <div className='warning-message'>
                    {t('cd_client_baja_blocked')}
                  </div>
                )}
                {shouldShowBypassButton && !shouldShowPartialWithBypass && (
                  <div className='warning-message d-flex gap-3 align-items-center'>
                    <SvgIcon
                      name='exclamationTriangle'
                      size='1.25rem'
                      stroke='var(--warning-500)'
                    />

                    <span>
                      {t('cd_client_baja')}
                      <span className='ms-2'>{t('cd_reason', { reason: bajaReason })}</span>
                    </span>

                    <Button
                      variant='naked'
                      size='sm'
                      disabled={temporarilyActivating}
                      label={
                        temporarilyActivating
                          ? t('cd_activating')
                          : t('cd_activate_temporarily')
                      }
                      onClick={() => setShowTempActivateConfirm(true)}
                    />
                  </div>
                )}
              </div>
              <div className='row mb-3'>
                <div className='col-12 col-lg-4'>
                  <Card specialPadding='p-4'>
                    <div className='d-flex mb-2 justify-content-between'>
                      <h3 className='caption fw-semibold'>{t('cd_info_label')}</h3>
                      {canToggleClientActive ? (
                        <div className='client-status-switch'>
                          <ActiveInactiveSwitch
                            value={!!clientActive}
                            onChange={handleClientActiveChange}
                            disabled={statusUpdating || !canToggleClientActive}
                          />
                        </div>
                      ) : (
                        <Badge
                          text={clientActive ? t('cd_status_active') : t('cd_status_inactive')}
                          color={statusColor(clientActive)}
                          size='sm'
                        />
                      )}
                    </div>

                    <p className='mini mb-1 font-light'>{t('cd_name_label')}</p>
                    <div className='mb-2 d-flex align-items-center gap-2'>
                      <SvgIcon name='person' size='1rem' fill='var(--dark)' />
                      <p className='caption d-flex align-items-center ms-1'>
                        {client.name}
                      </p>
                    </div>
                    <p className='mini mb-1 font-light'>{t('cd_rut_label')}</p>
                    <div className='mb-2 d-flex align-items-center gap-2'>
                      <SvgIcon name='person' size='1rem' fill='var(--dark)' />
                      <p className='caption d-flex align-items-center ms-1 '>
                        {client.id}
                      </p>
                    </div>
                    <p className='mini mb-1 font-light'>{t('cd_clave_unica_label')}</p>
                    <div className='mb-2 d-flex align-items-center justify-content-between gap-2'>
                      <div className='d-flex align-items-center'>
                        <SvgIcon
                          name='shield'
                          size='1rem'
                          stroke='var(--dark)'
                        />
                        <p className='caption ms-1'>
                          {showPassword ? client.claveUnica : '*****'}
                        </p>
                      </div>
                      <IconButton
                        ariaLabel={t('cd_aria_view')}
                        icon={showPassword ? 'eye' : 'eyeSlash'}
                        iconFill={!showPassword ? 'var(--dark)' : undefined}
                        iconStroke={showPassword ? 'var(--dark)' : undefined}
                        onClick={handleToggleClaveUnica}
                        disabled={loadingClaveUnica}
                        size='md'
                      />
                    </div>
                    <div className='d-flex align-items-center gap-2 mb-2'>
                      <SvgIcon name='phone' size='1rem' stroke='var(--dark)' />
                      <span className='caption'>{client.contact.phone}</span>
                    </div>
                    <div className='d-flex align-items-center gap-2 mb-2'>
                      <SvgIcon
                        name='fileEarmarkText'
                        size='1rem'
                        fill='var(--dark)'
                      />
                      <span className='caption'>{client.contact.email}</span>
                    </div>
                    <div className='d-flex align-items-start gap-2 mb-2'>
                      <SvgIcon name='geoAlt' size='1rem' fill='var(--dark)' />
                      <span className='caption'>{client.contact.address}</span>
                    </div>
                    <h3 className='caption fw-semibold mb-3 mt-3'>
                      {t('cd_sac_info_title')}
                    </h3>
                    <p className='mini mb-1 font-light'>{t('cd_sac_assigned')}</p>
                    <div className='mb-2 d-flex align-items-center'>
                      <SvgIcon name='person' size='1rem' fill='var(--dark)' />
                      <p className='caption d-flex align-items-center ms-1'>
                        {client.sac.agent}
                      </p>
                    </div>
                    <p className='mini mb-1 font-light'>{t('cd_branch_label')}</p>
                    <p className='caption mb-0'>{client.sac.branch}</p>
                  </Card>
                </div>

                <div className='col-12 col-lg-8'>
                  <Card specialPadding='p-4'>
                    <h3 className='caption fw-semibold mb-3'>{t('cd_cases_title')}</h3>
                    {casesLoading ? (
                      <p className='caption'>{t('cd_loading_cases')}</p>
                    ) : casesError ? (
                      <p className='caption text-danger'>{casesError}</p>
                    ) : clientCases.length ? (
                      <div className='caseCard caseCardInfo'>
                        <div className='row'>
                          {clientCases.map((item, index) => {
                            const caseId = item?.caseId ?? item?.id;
                            if (!caseId) return null;

                            const to = `/detalle-causa/${caseId}/movimientos`;
                            const { area, title, code, court } = getCaseFields(
                              item,
                              index
                            );
                            const caseStatus =
                              item?.internalStateStatus ?? null;
                            const statusClass =
                              caseStatus === 'Activa'
                                ? 'case-status-dot--active'
                                : 'case-status-dot--inactive';

                            return (
                              <div key={caseId ?? index} className='col-6 pb-3'>
                                {item?.blockManager && !canBypassClientBlock ? (
                                  <div
                                    className='caseCardItem blockedbybajas text-decoration-none justify-content-between'
                                    aria-disabled='true'
                                  >
                                    <span className='d-flex flex-column'>
                                      <span className='caption fw-semibold'>
                                        {title}
                                      </span>

                                      <div>
                                        {code !== t('cd_na') && (
                                          <span className='mini font-dark'>
                                            {t('cd_role_label', { code })}
                                          </span>
                                        )}

                                        {area !== t('cd_na') && (
                                          <span className='mini font-dark d-flex align-items-center gap-1'>
                                            <SvgIcon
                                              name='people'
                                              size='0.875rem'
                                              fill='var(--dark)'
                                            />
                                            {area}
                                          </span>
                                        )}

                                        {court !== t('cd_na') && (
                                          <span className='mini font-dark d-flex align-items-center gap-1'>
                                            <SvgIcon
                                              name='building2'
                                              size='0.875rem'
                                            />
                                            {court}
                                          </span>
                                        )}
                                      </div>
                                    </span>

                                    <span
                                      className={`case-status-dot ${statusClass}`}
                                      aria-label={caseStatus || t('cd_status_label')}
                                      title={caseStatus || t('cd_status_label')}
                                    />
                                  </div>
                                ) : (
                                  <Link
                                    to={to}
                                    className={`caseCardItem text-decoration-none justify-content-between${
                                      bajaHasClassification &&
                                      item?.blockManager === false
                                        ? ' warningbybajas'
                                        : ''
                                    }${
                                      item?.blockManager && canBypassClientBlock
                                        ? ' blockedbybajas can-bypass'
                                        : ''
                                    }`}
                                  >
                                    <span className='d-flex flex-column'>
                                      <span className='caption fw-semibold'>
                                        {title}
                                      </span>

                                      <div>
                                        {code !== t('cd_na') && (
                                          <span className='mini font-dark'>
                                            {t('cd_role_label', { code })}
                                          </span>
                                        )}

                                        {area !== t('cd_na') && (
                                          <span className='mini font-dark d-flex align-items-center gap-1'>
                                            <SvgIcon
                                              name='people'
                                              size='0.875rem'
                                              fill='var(--dark)'
                                            />
                                            {area}
                                          </span>
                                        )}

                                        {court !== t('cd_na') && (
                                          <span className='mini font-dark d-flex align-items-center gap-1'>
                                            <SvgIcon
                                              name='building2'
                                              size='0.875rem'
                                            />
                                            {court}
                                          </span>
                                        )}
                                      </div>
                                    </span>

                                    <span
                                      className={`case-status-dot ${statusClass}`}
                                      aria-label={caseStatus || t('cd_status_label')}
                                      title={caseStatus || t('cd_status_label')}
                                    />
                                  </Link>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <p className='caption mb-0'>
                        {t('cd_no_cases')}
                      </p>
                    )}
                  </Card>
                </div>
              </div>
              {!canBypassClientBlock && shouldBlockClient && (
                <BlockedClientMessage />
              )}
              {(canBypassClientBlock || !shouldBlockClient) && (
                <div className='row g-3'>
                  <div className='col-12'>
                    <Card specialPadding='p-0'>
                      <nav
                        role='tablist'
                        aria-label={t('cd_tabs_aria')}
                        className='d-flex gap-2 px-3 pt-3 border-bottom'
                        style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
                      >
                        {tabsInfo.map(
                          (tab) =>
                            tab.canAccess && (
                              <button
                                key={tab.key}
                                role='tab'
                                aria-selected={activeTabInfo === tab.key}
                                className={
                                  'btn btn-link text-decoration-none px-2 pb-3 btn-tab-border ' +
                                  (activeTabInfo === tab.key
                                    ? 'text-primary border-bottom border-2 border-primary'
                                    : 'text-body')
                                }
                                onClick={() => setActiveTabInfo(tab.key)}
                              >
                                {tab.label}
                              </button>
                            )
                        )}
                      </nav>
                      <div className='p-4'>
                        {LazyTabInfo ? (
                          <Suspense
                            fallback={<p className='caption'>{t('cd_loading_tab')}</p>}
                          >
                            <LazyTabInfo
                              DesignSystemComponents={DesignSystemComponents}
                              client={client}
                              clientRaw={clientRaw}
                              clientRut={clientRut}
                              setActiveTabInfo={setActiveTabInfo}
                              setActiveTabTasks={setActiveTabTasks}
                              setToggleSectionActive={setToggleSectionActive}
                              taskIdToOpen={taskIdToOpen}
                              setTaskIdToOpen={setTaskIdToOpen}
                            />
                          </Suspense>
                        ) : (
                          <p className='caption'>{t('cd_section_unavailable')}</p>
                        )}
                      </div>
                    </Card>
                  </div>
                </div>
              )}
              {showEditModal && (
                <ClientsCreateModal
                  DesignSystemComponents={DesignSystemComponents}
                  show={showEditModal}
                  onClose={() => setShowEditModal(false)}
                  mode='edit'
                  initialData={editInitialData}
                  onSave={() => setRefreshKey((prev) => prev + 1)}
                />
              )}
              {Modal && (
                <ConfirmModal
                  Modal={Modal}
                  show={showTempActivateConfirm}
                  title={t('cd_confirm_temp_activate_title')}
                  message={t('cd_confirm_temp_activate_msg')}
                  confirmLabel={t('cd_confirm_label')}
                  confirmVariant='primary'
                  onConfirm={handleTemporarilyActivate}
                  onClose={() => setShowTempActivateConfirm(false)}
                />
              )}
            </div>
          )}
          {toggleSectionActive === 'second' && (
            <div>
              <div>
                {hasPatrocinio && (
                  <div className='warning-message d-flex gap-3 align-items-center'>
                    <SvgIcon
                      name='exclamationTriangle'
                      size='1.25rem'
                      stroke='var(--warning-500)'
                    />
                    {patrocinioDetail && (
                      <span className='ms-2'>{patrocinioDetail}</span>
                    )}
                    <p className='caption fw-semibold font-light'>
                      {t('cd_temporarily_active')}
                    </p>
                  </div>
                )}
                {shouldShowPartialWithBypass && (
                  <div className='warning-message d-flex gap-3 align-items-center'>
                    <SvgIcon
                      name='exclamationTriangle'
                      size='1.25rem'
                      stroke='var(--warning-500)'
                    />
                    <span>{t('cd_client_has_baja_cases')}</span>
                    <Button
                      variant='naked'
                      size='sm'
                      disabled={temporarilyActivating}
                      label={
                        temporarilyActivating
                          ? t('cd_activating')
                          : t('cd_activate_temporarily')
                      }
                      onClick={() => setShowTempActivateConfirm(true)}
                    />
                  </div>
                )}
                {!shouldShowPartialWithBypass && shouldShowPartialBaja && (
                  <div className='warning-message'>
                    {t('cd_client_has_baja_cases')}
                  </div>
                )}
                {shouldShowMixedStateWarning && (
                  <div className='warning-message'>
                    {t('cd_mixed_state_warning')}
                  </div>
                )}
                {shouldShowManagerWarning && (
                  <div className='warning-message'>
                    {t('cd_manager_warning')}
                  </div>
                )}
                {!canBypassClientBlock && shouldBlockClient && (
                  <div className='warning-message'>
                    {t('cd_client_baja_blocked_short')}
                  </div>
                )}
                {shouldShowBypassButton && !shouldShowPartialWithBypass && (
                  <div className='warning-message d-flex gap-3 align-items-center'>
                    <SvgIcon
                      name='exclamationTriangle'
                      size='1.25rem'
                      stroke='var(--warning-500)'
                    />
                    <span>
                      {t('cd_client_baja')}
                      <span className='ms-2'>{t('cd_reason', { reason: bajaReason })}</span>
                    </span>
                    <Button
                      variant='naked'
                      size='sm'
                      disabled={temporarilyActivating}
                      label={
                        temporarilyActivating
                          ? t('cd_activating')
                          : t('cd_activate_temporarily')
                      }
                      onClick={() => setShowTempActivateConfirm(true)}
                    />
                  </div>
                )}
              </div>
              <div className='row'>
                <div className='col-8'>
                  {!canBypassClientBlock && shouldBlockClient && (
                    <BlockedClientMessage />
                  )}
                  {(canBypassClientBlock || !shouldBlockClient) && (
                    <div className='row g-3'>
                      <div className='col-12'>
                        <Card specialPadding='p-0'>
                          <nav
                            role='tablist'
                            aria-label={t('cd_tabs_aria')}
                            className='d-flex gap-2 px-3 pt-3 border-bottom'
                            style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
                          >
                            {tabsTasks.map(
                              (tab) =>
                                tab.canAccess && (
                                  <button
                                    key={tab.key}
                                    role='tab'
                                    aria-selected={activeTabTasks === tab.key}
                                    className={
                                      'btn btn-link text-decoration-none px-2 pb-3 btn-tab-border ' +
                                      (activeTabTasks === tab.key
                                        ? 'text-primary border-bottom border-2 border-primary'
                                        : 'text-body')
                                    }
                                    onClick={() => setActiveTabTasks(tab.key)}
                                  >
                                    {tab.label}
                                  </button>
                                )
                            )}
                          </nav>
                          <div className='p-4'>
                            {LazyTabTasks ? (
                              <Suspense
                                fallback={
                                  <p className='caption'>{t('cd_loading_tab')}</p>
                                }
                              >
                                <LazyTabTasks
                                  DesignSystemComponents={
                                    DesignSystemComponents
                                  }
                                  client={client}
                                  clientRaw={clientRaw}
                                  clientRut={clientRut}
                                  setActiveTab={setActiveTabTasks}
                                  taskIdToOpen={taskIdToOpen}
                                  setTaskIdToOpen={setTaskIdToOpen}
                                />
                              </Suspense>
                            ) : (
                              <p className='caption'>{t('cd_section_unavailable')}</p>
                            )}
                          </div>
                        </Card>
                      </div>
                    </div>
                  )}
                </div>
                <div className='col-4'>
                  <div className='case-info-sticky-wrap'>
                    <div className='case-info-scroll'>
                      <div className='row mb-3'>
                        <div className='col-12 mb-4'>
                          <Card specialPadding='p-4'>
                            <div className='d-flex mb-2 justify-content-between'>
                              <h3 className='caption fw-semibold'>
                                {t('cd_info_label')}
                              </h3>
                              {canToggleClientActive ? (
                                <div className='client-status-switch'>
                                  <ActiveInactiveSwitch
                                    value={!!clientActive}
                                    onChange={handleClientActiveChange}
                                    disabled={statusUpdating}
                                  />
                                </div>
                              ) : (
                                <Badge
                                  text={clientActive ? t('cd_status_active') : t('cd_status_inactive')}
                                  color={statusColor(clientActive)}
                                  size='sm'
                                />
                              )}
                            </div>

                            <p className='mini mb-1 font-light'>{t('cd_name_label')}</p>
                            <div className='mb-2 d-flex align-items-center gap-2'>
                              <SvgIcon
                                name='person'
                                size='1rem'
                                fill='var(--dark)'
                              />
                              <p className='caption d-flex align-items-center ms-1'>
                                {client.name}
                              </p>
                            </div>
                            <p className='mini mb-1 font-light'>{t('cd_rut_label')}</p>
                            <div className='mb-2 d-flex align-items-center gap-2'>
                              <SvgIcon
                                name='person'
                                size='1rem'
                                fill='var(--dark)'
                              />
                              <p className='caption d-flex align-items-center ms-1 '>
                                {client.id}
                              </p>
                            </div>
                            <p className='mini mb-1 font-light'>{t('cd_clave_unica_label')}</p>
                            <div className='mb-2 d-flex align-items-center justify-content-between gap-2'>
                              <div className='d-flex align-items-center'>
                                <SvgIcon
                                  name='shield'
                                  size='1rem'
                                  stroke='var(--dark)'
                                />
                                <p className='caption ms-1'>
                                  {showPassword ? client.claveUnica : '*****'}
                                </p>
                              </div>
                              <IconButton
                                ariaLabel={t('cd_aria_view')}
                                icon={showPassword ? 'eye' : 'eyeSlash'}
                                iconFill={
                                  !showPassword ? 'var(--dark)' : undefined
                                }
                                iconStroke={
                                  showPassword ? 'var(--dark)' : undefined
                                }
                                onClick={handleToggleClaveUnica}
                                disabled={loadingClaveUnica}
                                size='md'
                              />
                            </div>
                            <div className='d-flex align-items-center gap-2 mb-2'>
                              <SvgIcon
                                name='phone'
                                size='1rem'
                                stroke='var(--dark)'
                              />
                              <span className='caption'>
                                {client.contact.phone}
                              </span>
                            </div>
                            <div className='d-flex align-items-center gap-2 mb-2'>
                              <SvgIcon
                                name='fileEarmarkText'
                                size='1rem'
                                fill='var(--dark)'
                              />
                              <span className='caption'>
                                {client.contact.email}
                              </span>
                            </div>
                            <div className='d-flex align-items-start gap-2'>
                              <SvgIcon
                                name='geoAlt'
                                size='1rem'
                                fill='var(--dark)'
                              />
                              <span className='caption'>
                                {client.contact.address}
                              </span>
                            </div>
                            <h3 className='caption fw-semibold mb-3 mt-3'>
                              {t('cd_sac_info_title')}
                            </h3>
                            <p className='mini mb-1 font-light'>
                              {t('cd_sac_assigned')}
                            </p>
                            <div className='mb-2 d-flex align-items-center'>
                              <SvgIcon
                                name='person'
                                size='1rem'
                                fill='var(--dark)'
                              />
                              <p className='caption d-flex align-items-center ms-1'>
                                {client.sac.agent}
                              </p>
                            </div>
                            <p className='mini mb-1 font-light'>{t('cd_branch_label')}</p>
                            <p className='caption mb-0'>{client.sac.branch}</p>
                          </Card>
                        </div>
                        <div className='col-12'>
                          <Card specialPadding='p-4'>
                            <h3 className='caption fw-semibold mb-3'>{t('cd_cases_title')}</h3>
                            {casesLoading ? (
                              <p className='caption'>{t('cd_loading_cases')}</p>
                            ) : casesError ? (
                              <p className='caption text-danger'>
                                {casesError}
                              </p>
                            ) : clientCases.length ? (
                              <div className='caseCard caseCardTasks'>
                                <div className='row'>
                                  {clientCases.map((item, index) => {
                                    const caseId = item?.caseId ?? item?.id;
                                    if (!caseId) return null;

                                    const to = `/detalle-causa/${caseId}/movimientos`;
                                    const { area, title, code, court } =
                                      getCaseFields(item, index);
                                    const caseStatus =
                                      item?.internalStateStatus ??
                                      item?.caseStateProcessName ??
                                      item?.caseStateProcess ??
                                      item?.state ??
                                      null;
                                    const statusClass =
                                      caseStatus &&
                                      String(caseStatus)
                                        .toLowerCase()
                                        .includes('activo')
                                        ? 'case-status-dot--active'
                                        : 'case-status-dot--inactive';

                                    return (
                                      <div
                                        key={caseId ?? index}
                                        className='col-12 pb-3'
                                      >
                                        {item?.blockManager &&
                                        !canBypassClientBlock ? (
                                          <div
                                            className='caseCardItem blockedbybajas text-decoration-none justify-content-between'
                                            aria-disabled='true'
                                          >
                                            <span className='d-flex flex-column'>
                                              <span className='caption fw-semibold'>
                                                {title}
                                              </span>

                                              <div>
                                                {code !== t('cd_na') && (
                                                  <span className='mini font-dark'>
                                                    {t('cd_role_label', { code })}
                                                  </span>
                                                )}

                                                {area !== t('cd_na') && (
                                                  <span className='mini font-dark d-flex align-items-center gap-1'>
                                                    <SvgIcon
                                                      name='people'
                                                      size='0.875rem'
                                                      fill='var(--dark)'
                                                    />
                                                    {area}
                                                  </span>
                                                )}

                                                {court !== t('cd_na') && (
                                                  <span className='mini font-dark d-flex align-items-center gap-1'>
                                                    <SvgIcon
                                                      name='building2'
                                                      size='0.875rem'
                                                    />
                                                    {court}
                                                  </span>
                                                )}
                                              </div>
                                            </span>

                                            <span
                                              className={`case-status-dot ${statusClass}`}
                                              aria-label={
                                                caseStatus || t('cd_status_label')
                                              }
                                              title={caseStatus || t('cd_status_label')}
                                            />
                                          </div>
                                        ) : (
                                          <Link
                                            to={to}
                                            className={`caseCardItem text-decoration-none justify-content-between${
                                              bajaHasClassification &&
                                              item?.blockManager === false
                                                ? ' warningbybajas'
                                                : ''
                                            }${
                                              item?.blockManager &&
                                              canBypassClientBlock
                                                ? ' blockedbybajas can-bypass'
                                                : ''
                                            }`}
                                          >
                                            <span className='d-flex flex-column'>
                                              <span className=' fw-semibold'>
                                                {title}
                                              </span>

                                              <div>
                                                {code !== t('cd_na') && (
                                                  <span className='mini '>
                                                    {t('cd_role_label', { code })}
                                                  </span>
                                                )}

                                                {area !== t('cd_na') && (
                                                  <span className='mini d-flex align-items-center gap-1'>
                                                    <SvgIcon
                                                      name='people'
                                                      size='0.875rem'
                                                      fill='var(--dark)'
                                                    />
                                                    {area}
                                                  </span>
                                                )}

                                                {court !== t('cd_na') && (
                                                  <span className='mini font-dark d-flex align-items-center gap-1'>
                                                    <SvgIcon
                                                      name='building2'
                                                      size='0.875rem'
                                                    />
                                                    {court}
                                                  </span>
                                                )}
                                              </div>
                                            </span>

                                            <span
                                              className={`case-status-dot ${statusClass}`}
                                              aria-label={
                                                caseStatus || t('cd_status_label')
                                              }
                                              title={caseStatus || t('cd_status_label')}
                                            />
                                          </Link>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <p className='caption mb-0'>
                                {t('cd_no_cases')}
                              </p>
                            )}
                          </Card>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {showEditModal && (
                <ClientsCreateModal
                  DesignSystemComponents={DesignSystemComponents}
                  show={showEditModal}
                  onClose={() => setShowEditModal(false)}
                  mode='edit'
                  initialData={editInitialData}
                  onSave={() => setRefreshKey((prev) => prev + 1)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientsDetail;
